import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { myClientsService } from '@/features/my-clients/services/my-clients.service';
import { ClientDetailTabs } from '@/features/clients/components/ClientDetailTabs';
import { getClientDisplayName } from '@/features/clients/utils/display';
import type { Client } from '@/features/clients/services/clients.service';
import { useAuthStore } from '@/store/auth.store';
import { Permission } from '@/common/enums/permissions.enum';

export const Route = createFileRoute('/_authenticated/my-clients/$id')({
  validateSearch: (search: Record<string, unknown>): { tab?: 'agent' } =>
    search.tab === 'agent' ? { tab: 'agent' } : {},
  component: MyClientDetailPage,
});

function MyClientDetailPage() {
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const manageAll = hasPermission(Permission.MY_CLIENTS_MANAGE_ALL);
  const canUseAgents = hasPermission(Permission.AI_AGENTS_USE);

  const { data: client, isLoading } = useQuery({
    queryKey: ['my-client', id],
    queryFn: () => myClientsService.getById(id),
  });

  if (isLoading) {
    return <div className="text-muted-foreground">Carregando cliente...</div>;
  }

  if (!client) {
    return <div className="text-muted-foreground">Cliente não encontrado</div>;
  }

  const isAtendimento = client.myCargo?.key === 'atendimento';
  const canEditContacts = manageAll || isAtendimento;
  const canEditCentral = manageAll || isAtendimento;
  const canEditAttendants = manageAll;
  const canEditMonthlyScope = manageAll;
  const canEditScope = manageAll || isAtendimento;

  const clientForTabs: Client = {
    id: client.id,
    tenantId: '',
    name: client.name,
    tradeName: client.tradeName,
    cnpj: client.cnpj,
    openingDate: client.openingDate,
    isActive: client.isActive,
    clientType: client.clientType,
    notebookLmUrl: client.notebookLmUrl,
    notionBoard: client.notionBoard,
    accountManagerId: null,
    companyId: null,
    reimbursementMonthlyBudget: null,
    notionBoardId: client.notionBoard?.id ?? null,
    createdAt: '',
    updatedAt: '',
    accountManager: null,
    company: null,
    contacts: [],
    contracts: [],
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: '/my-clients' })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{getClientDisplayName(client)}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline">
              {client.myCargo
                ? `Cargo: ${client.myCargo.name}`
                : 'Sem vínculo'}
            </Badge>
            <Badge variant={client.isActive ? 'default' : 'secondary'}>
              {client.isActive ? 'Ativo' : 'Inativo'}
            </Badge>
            {manageAll && (
              <Badge variant="secondary">Visão completa</Badge>
            )}
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Área do cliente</CardTitle>
        </CardHeader>
        <CardContent>
          <ClientDetailTabs
            client={clientForTabs}
            tabs={[
              'contacts',
              'attendants',
              'central',
              'scope',
              ...(canUseAgents ? (['agent'] as const) : []),
            ]}
            defaultTab={tab === 'agent' && canUseAgents ? 'agent' : 'contacts'}
            apiScope="my"
            canEditContacts={canEditContacts}
            canEditCentral={canEditCentral}
            canEditAttendants={canEditAttendants}
            canEditScope={canEditScope}
            canEditMonthlyScope={canEditMonthlyScope}
          />
        </CardContent>
      </Card>
    </div>
  );
}
